import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DatabaseQueryLogger = ({ applicationId, isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [storageCheck, setStorageCheck] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const addLog = (level, category, message, data = null) => {
    const timestamp = new Date()?.toISOString();
    const logEntry = {
      id: `${timestamp}-${Math.random()}`,
      timestamp,
      level, // 'info', 'success', 'warning', 'error'
      category, // 'DATABASE', 'STORAGE', 'VALIDATION'
      message,
      data
    };
    setLogs(prev => [...prev, logEntry]);
    console.log(`[${level?.toUpperCase()}] [${category}]`, message, data || '');
  };

  const clearLogs = () => {
    setLogs([]);
    setDocumentInfo(null);
    setStorageCheck(null);
  };

  const runDiagnostics = async () => {
    clearLogs();
    setIsLoading(true);
    addLog('info', 'SYSTEM', '=== STARTING REAL-TIME DATABASE DIAGNOSTICS ===');
    addLog('info', 'SYSTEM', `Application ID: ${applicationId}`);

    try {
      // Step 1: Query database for application
      addLog('info', 'DATABASE', 'Step 1: Querying applications table');
      const { data: appData, error: appError } = await supabase?.from('applications')?.select('*')?.eq('id', applicationId)?.single();

      if (appError) {
        addLog('error', 'DATABASE', 'Failed to query application', {
          error: appError?.message,
          code: appError?.code,
          details: appError?.details
        });
        throw appError;
      }

      addLog('success', 'DATABASE', 'Application found in database', {
        id: appData?.id,
        customer_name: appData?.customer_name,
        application_status: appData?.application_status,
        application_date: appData?.application_date
      });

      // Step 2: Check document URL field
      addLog('info', 'DATABASE', 'Step 2: Checking vehicle_document_url field');
      const documentUrl = appData?.vehicle_document_url;

      if (!documentUrl) {
        addLog('warning', 'DATABASE', 'vehicle_document_url is NULL or empty');
        setDocumentInfo({
          exists: false,
          url: null,
          reason: 'No document URL in database'
        });
        return;
      }

      addLog('success', 'DATABASE', 'Document URL exists in database', {
        url: documentUrl,
        length: documentUrl?.length
      });

      setDocumentInfo({
        exists: true,
        url: documentUrl,
        rawUrl: documentUrl
      });

      // Step 3: Parse storage path
      addLog('info', 'VALIDATION', 'Step 3: Parsing storage path from URL');
      const extractedPath = extractStoragePath(documentUrl);

      if (!extractedPath) {
        addLog('error', 'VALIDATION', 'Failed to extract storage path from URL', {
          url: documentUrl
        });
        setDocumentInfo(prev => ({
          ...prev,
          pathExtracted: false,
          error: 'Invalid URL format'
        }));
        return;
      }

      addLog('success', 'VALIDATION', 'Storage path extracted successfully', {
        path: extractedPath,
        bucket: 'vehicle-documents'
      });

      setDocumentInfo(prev => ({
        ...prev,
        pathExtracted: true,
        storagePath: extractedPath,
        bucket: 'vehicle-documents'
      }));

      // Step 4: Check if file exists in storage
      addLog('info', 'STORAGE', 'Step 4: Checking if file exists in Supabase Storage');
      addLog('info', 'STORAGE', `Attempting to download: bucket=vehicle-documents, path=${extractedPath}`);

      const { data: fileData, error: storageError } = await supabase?.storage?.from('vehicle-documents')?.download(extractedPath);

      if (storageError) {
        addLog('error', 'STORAGE', 'File does NOT exist in storage or access denied', {
          error: storageError?.message,
          statusCode: storageError?.statusCode,
          path: extractedPath
        });
        setStorageCheck({
          exists: false,
          error: storageError?.message,
          statusCode: storageError?.statusCode
        });
        return;
      }

      if (!fileData) {
        addLog('error', 'STORAGE', 'Download returned no data');
        setStorageCheck({
          exists: false,
          error: 'No data returned from storage'
        });
        return;
      }

      addLog('success', 'STORAGE', 'File EXISTS in storage and is accessible', {
        size: fileData?.size,
        type: fileData?.type,
        sizeKB: (fileData?.size / 1024)?.toFixed(2)
      });

      setStorageCheck({
        exists: true,
        size: fileData?.size,
        type: fileData?.type,
        sizeKB: (fileData?.size / 1024)?.toFixed(2),
        sizeMB: (fileData?.size / (1024 * 1024))?.toFixed(2)
      });

      // Step 5: Validate file type
      addLog('info', 'VALIDATION', 'Step 5: Validating file type');
      const detectedType = detectFileType(extractedPath);
      addLog('info', 'VALIDATION', `Detected file type: ${detectedType}`, {
        path: extractedPath,
        mimeType: fileData?.type
      });

      addLog('success', 'SYSTEM', '=== DIAGNOSTICS COMPLETED SUCCESSFULLY ===');

    } catch (error) {
      addLog('error', 'SYSTEM', '=== DIAGNOSTICS FAILED ===', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });
    } finally {
      setIsLoading(false);
    }
  };

  const extractStoragePath = (url) => {
    if (!url) return null;

    const cleanUrl = url?.split('?')?.[0]?.split('#')?.[0];

    // Method 1: Full Supabase storage URL
    if (cleanUrl?.includes('/storage/v1/object/')) {
      const parts = cleanUrl?.split('/storage/v1/object/');
      if (parts?.length > 1) {
        let path = parts?.[1]?.replace(/^(public|authenticated)\//g, '');
        path = path?.replace(/^vehicle-documents\//g, '');
        return path;
      }
    }

    // Method 2: Contains 'vehicle-documents/'
    if (cleanUrl?.includes('vehicle-documents/')) {
      const index = cleanUrl?.indexOf('vehicle-documents/');
      return cleanUrl?.substring(index + 'vehicle-documents/'?.length);
    }

    // Method 3: Already clean path
    if (!cleanUrl?.includes('http') && !cleanUrl?.includes('supabase')) {
      return cleanUrl;
    }

    return null;
  };

  const detectFileType = (path) => {
    const lower = path?.toLowerCase();
    if (lower?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) return 'image';
    if (lower?.match(/\.pdf$/)) return 'pdf';
    return 'unknown';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'success': return 'check_circle';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  useEffect(() => {
    if (isOpen && applicationId) {
      runDiagnostics();
    }
  }, [isOpen, applicationId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Icon name="Bug" className="text-blue-600" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Real-Time Database Query Logger</h2>
              <p className="text-sm text-gray-500">Document Existence & Storage Path Diagnostics</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Database Status */}
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Database" className="text-blue-600" size={20} />
                <h3 className="font-semibold text-gray-900">Database Status</h3>
              </div>
              {documentInfo ? (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={documentInfo?.exists ? 'CheckCircle' : 'XCircle'} 
                      className={documentInfo?.exists ? 'text-green-600' : 'text-red-600'}
                      size={16}
                    />
                    <span>{documentInfo?.exists ? 'URL Found' : 'No URL'}</span>
                  </div>
                  {documentInfo?.url && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs break-all">
                      {documentInfo?.url?.substring(0, 80)}...
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Waiting for query...</p>
              )}
            </div>

            {/* Storage Path */}
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Folder" className="text-purple-600" size={20} />
                <h3 className="font-semibold text-gray-900">Storage Path</h3>
              </div>
              {documentInfo?.pathExtracted ? (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" className="text-green-600" size={16} />
                    <span>Path Extracted</span>
                  </div>
                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs break-all">
                    <strong>Bucket:</strong> {documentInfo?.bucket}<br />
                    <strong>Path:</strong> {documentInfo?.storagePath}
                  </div>
                </div>
              ) : documentInfo?.pathExtracted === false ? (
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="AlertCircle" className="text-red-600" size={16} />
                  <span>Extraction Failed</span>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Waiting for extraction...</p>
              )}
            </div>

            {/* Storage Retrieval */}
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CloudDownload" className="text-green-600" size={20} />
                <h3 className="font-semibold text-gray-900">Storage Retrieval</h3>
              </div>
              {storageCheck ? (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={storageCheck?.exists ? 'CheckCircle' : 'XCircle'} 
                      className={storageCheck?.exists ? 'text-green-600' : 'text-red-600'}
                      size={16}
                    />
                    <span>{storageCheck?.exists ? 'File Exists' : 'Not Found'}</span>
                  </div>
                  {storageCheck?.exists ? (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                      <strong>Size:</strong> {storageCheck?.sizeKB} KB ({storageCheck?.sizeMB} MB)<br />
                      <strong>Type:</strong> {storageCheck?.type}
                    </div>
                  ) : (
                    <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                      {storageCheck?.error}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Waiting for check...</p>
              )}
            </div>
          </div>
        </div>

        {/* Logs Section */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-gray-600 hover:text-gray-900"
              >
                <Icon name={expanded ? 'ChevronDown' : 'ChevronRight'} size={20} />
              </button>
              <h3 className="font-semibold text-gray-900">Detailed Query Logs ({logs?.length})</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={runDiagnostics}
                disabled={isLoading}
                variant="outline"
                size="sm"
                iconName="RefreshCw"
              >
                <Icon name="RefreshCw" size={16} />
                Re-run
              </Button>
              <Button
                onClick={clearLogs}
                variant="outline"
                size="sm"
                iconName="Trash2"
              >
                <Icon name="Trash2" size={16} />
                Clear
              </Button>
            </div>
          </div>

          {expanded && (
            <div className="flex-1 overflow-y-auto p-4 bg-gray-900 text-gray-100 font-mono text-xs">
              {isLoading && logs?.length === 0 && (
                <div className="flex items-center gap-2 text-yellow-400">
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span>Running diagnostics...</span>
                </div>
              )}
              {logs?.map((log) => (
                <div key={log?.id} className="mb-2 pb-2 border-b border-gray-700">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 text-[10px] w-24 flex-shrink-0">
                      {new Date(log.timestamp)?.toLocaleTimeString('de-DE', { 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        second: '2-digit',
                        fractionalSecondDigits: 3
                      })}
                    </span>
                    <Icon 
                      name={getLevelIcon(log?.level)} 
                      className={getLevelColor(log?.level)}
                      size={16}
                    />
                    <span className="text-blue-400 w-24 flex-shrink-0">[{log?.category}]</span>
                    <span className="flex-1">{log?.message}</span>
                  </div>
                  {log?.data && (
                    <div className="ml-28 mt-1 p-2 bg-gray-800 rounded text-[10px] overflow-x-auto">
                      <pre>{JSON.stringify(log?.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <Icon name="Info" size={16} className="inline mr-1" />
            This tool queries the database in real-time to diagnose document display issues
          </div>
          <Button onClick={onClose} variant="primary" iconName="Close">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseQueryLogger;